import { Link } from 'react-router'
import { useCopy, useLocale } from '../../content'
import { grouped } from '../../data/format'
import { PROVINCE_AREA_KM2, VC_COMUNI, PROVINCE_RICE_HA } from '../../data/servedComuni'
import { pathFor } from '../../routes'
import { Eyebrow } from '../layout/Eyebrow'
import { Section } from '../layout/Section'
import { ZoneMap } from '../zone/ZoneMap'

/** The comuni count comes from the list itself, so it cannot drift from the Zone page. */
const PROVINCE_COUNT = VC_COMUNI.length

/**
 * The home's zone band (ported from VV2's, without the cinema bars): the
 * kicker and title with a one-line intro, the territory map in its compact
 * form, and three figures with the link to the Zone page.
 *
 * It follows the Numeri band (paper, closed by its own bottom rule) and
 * precedes the FAQ on paper too, so a bottom hairline marks that seam — the
 * map's label halo is paper, so the band itself stays paper. From lg the map
 * takes the wider column and spans both rows; the heading (with its intro)
 * and the figures (with the link) keep their DOM order around the map and
 * sit either side of the column's middle. `grid-cols-1` matters below lg: an
 * implicit `auto` track would size to the map's 45rem phone minimum and push
 * the page past the viewport.
 *
 * The three figures come in like credits (`data-reveal`, one per row). Rows
 * share one footprint: the number sits in a fixed 11rem slot from sm up, so
 * the labels start on one line whatever the number's width.
 */
export function ZoneBand() {
  const copy = useCopy()
  const locale = useLocale()
  const z = copy.zone
  const figures: readonly [string, string][] = [
    [String(PROVINCE_COUNT), z.home.figures.places],
    [`${grouped(PROVINCE_AREA_KM2, locale)} km²`, z.home.figures.area],
    [`${grouped(PROVINCE_RICE_HA, locale)} ha`, z.home.figures.rice],
  ]
  return (
    <Section id="zone" className="border-b border-vc2-hairline">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-x-16 lg:gap-y-10">
        <div className="lg:self-end">
          <Eyebrow>{z.eyebrow}</Eyebrow>
          <h2 className="vc2-display mt-4 text-4xl md:text-5xl">{z.title}</h2>
          <p className="mt-4 max-w-md text-vc2-ink-soft">{z.home.intro}</p>
        </div>
        <div className="lg:col-start-2 lg:row-span-2 lg:row-start-1">
          <ZoneMap compact />
        </div>
        <div className="lg:self-start">
          <ul className="divide-y divide-vc2-hairline border-y border-vc2-hairline">
            {figures.map(([value, label]) => (
              <li key={label} data-reveal className="py-4 sm:flex sm:items-baseline sm:gap-5">
                <span className="vc2-display block whitespace-nowrap text-[1.75rem] leading-tight tabular-nums sm:w-44 sm:shrink-0">
                  {value}
                </span>
                <span className="mt-1 block text-vc2-ink-soft sm:mt-0">{label}</span>
              </li>
            ))}
          </ul>
          <Link
            to={pathFor('zone', locale)}
            className="mt-8 inline-block text-sm font-semibold text-vc2-ink underline decoration-vc2-accent underline-offset-4 hover:decoration-current"
          >
            {z.home.more} <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </Section>
  )
}
